import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  Heart,
  Trophy,
  RotateCcw,
  EyeOff,
  ZoomIn,
  ArrowRight,
  Check,
  X as XIcon,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  Sparkles,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import './tower-game.css';
import {
  TOWER_QUESTIONS,
  LUCKY_WHEEL_PRIZES,
  TowerQuestion,
  ChoiceQuestion,
  OrderQuestion,
  MatchQuestion,
  LuckyWheelPrize,
} from '../../data/quizData';

/* ------------------ WebAudio Synthesizer ------------------ */
let audioCtx: AudioContext | null = null;
function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const AudioCtxClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtxClass) {
        audioCtx = new AudioCtxClass();
      }
    }
  } catch (_) {}
  return audioCtx;
}

function tone(freq: number, dur = 0.12, type: OscillatorType = 'sine', delay = 0, vol = 0.18) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur);
  } catch (_) {}
}

const sfx = {
  tap: () => tone(520, 0.07, 'triangle', 0, 0.1),
  correct: () => {
    tone(660, 0.12);
    tone(880, 0.18, 'sine', 0.12);
  },
  wrong: () => tone(170, 0.3, 'sawtooth', 0, 0.12),
  ascend: () => {
    // Ascending pentatonic chord / temple bell chime: C5, E5, G5, A5, C6
    const notes = [523.25, 659.25, 783.99, 880.0, 1046.5];
    notes.forEach((freq, i) => {
      tone(freq, 0.3, 'sine', i * 0.07, 0.14 - i * 0.015);
    });
  },
  spin: () => {
    const ctx = getAudioContext();
    if (!ctx) return;
    for (let i = 0; i < 14; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      const t = ctx.currentTime + i * 0.22 * (1 + i * 0.05);
      osc.frequency.setValueAtTime(450 + (i % 3) * 60, t);
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.045);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.05);
    }
  },
  win: () => {
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      tone(freq, 0.25, 'sine', i * 0.1, 0.2);
    });
  },
};

/* ------------------ Tower Constants ------------------ */
const STRIP_BTN_Y: Record<number, number> = { 1: 48, 2: 50, 3: 50, 4: 50, 5: 50, 6: 78 };
const TOWER_W = 1024;
const TOWER_H = 1723;
const STRIP_H: Record<number, number> = { 1: 299, 2: 194, 3: 207, 4: 217, 5: 227, 6: 579 };

interface TowerGameProps {
  mode?: 'normal' | 'competition';
  onClose: () => void;
  onOpenLeaderboard: () => void;
}

export const TowerGame: React.FC<TowerGameProps> = ({
  mode = 'normal',
  onClose,
  onOpenLeaderboard,
}) => {
  // Navigation: 'overview' (Trang tháp demo) | 'play' (Màn hình trả lời câu hỏi)
  const [screen, setScreen] = useState<'overview' | 'play'>('overview');
  const [floor, setFloor] = useState<number>(1);
  const [bestFloor, setBestFloor] = useState<number>(1);
  const [hearts, setHearts] = useState<number>(3);
  const [correctAnswers, setCorrectAnswers] = useState<number>(0);
  const [gameStatus, setGameStatus] = useState<'playing' | 'reward' | 'lost' | 'victory'>('playing');

  // Inventory & Collectibles won from lucky wheel
  const [inventory, setInventory] = useState<LuckyWheelPrize[]>([]);
  const [showInventoryModal, setShowInventoryModal] = useState<boolean>(false);

  // Question state
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState<number>(0);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [revealedArtwork, setRevealedArtwork] = useState<boolean>(false);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState<boolean>(false);
  const [isCorrect, setIsCorrect] = useState<boolean>(false);

  // Word arrangement state (Vua Tiếng Việt)
  const [scrambledLetters, setScrambledLetters] = useState<{ id: number; char: string; used: boolean }[]>([]);
  const [placedLetters, setPlacedLetters] = useState<{ id: number; char: string }[]>([]);

  // Matching pairs state (Interactive 2-column SVG matching)
  const [matchPairsMap, setMatchPairsMap] = useState<Record<string, string>>({});
  const [selectedLeftItem, setSelectedLeftItem] = useState<string | null>(null);
  const [verifiedPairs, setVerifiedPairs] = useState<{ correct: Record<string, boolean>; wrong: Record<string, boolean> } | null>(null);
  const matchContainerRef = useRef<HTMLDivElement>(null);
  const [matchLines, setMatchLines] = useState<{
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    status: 'normal' | 'correct' | 'wrong';
  }[]>([]);

  // Lucky wheel state (P1.2)
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [wheelRotation, setWheelRotation] = useState<number>(0);
  const [prizeWon, setPrizeWon] = useState<LuckyWheelPrize | null>(null);
  const [spinCount, setSpinCount] = useState<number>(1);

  // Navigation transition direction for iOS-style page slides
  const [navDirection, setNavDirection] = useState<'forward' | 'back'>('forward');

  // Tower DOM layout refs
  const wrapRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);

  // Current question from TOWER_QUESTIONS
  const currentQ: TowerQuestion = useMemo(() => {
    return TOWER_QUESTIONS[currentQuestionIdx % TOWER_QUESTIONS.length];
  }, [currentQuestionIdx]);

  // Shuffled right items for matching questions
  const shuffledMatchRights = useMemo(() => {
    if (currentQ.type !== 'match') return [];
    const rights = (currentQ as MatchQuestion).pairs.map((p) => p.right);
    return [...rights].sort(() => Math.random() - 0.5);
  }, [currentQ]);

  // Dynamic visible floor count to adaptively fill screen height without top gaps
  const [visibleCount, setVisibleCount] = useState<number>(10);

  // Strip layout computation: modular pagoda gallery tiers (2..5) cycling seamlessly
  const towerStrips = useMemo(() => {
    const current = floor;
    const start = Math.max(1, current <= 3 ? 1 : current - 2);
    const slots = Array.from({ length: visibleCount }, (_, i) => visibleCount - i);

    return slots.map((slot) => {
      const fn = start + slot - 1;
      const isCurrent = fn === current;
      const isDone = fn < current;
      const isGhost = fn > current;
      const distance = fn - current;

      // Authentic Vietnamese Pagoda correspondence:
      // Floor 1 -> Strip 1 (Ground Base Gate: bệ đá hoa sen, đôi Nghê đá, cửa son thếp vàng)
      // Modular gallery tiers (2..5) cycle seamlessly:
      // Floor 2 -> Strip 2 (Gốm Chu Đậu men lam)
      // Floor 3 -> Strip 3 (Sơn mài phù điêu Bồ Đề)
      // Floor 4 -> Strip 4 (Mộc bản dân gian Thánh Gióng)
      // Floor 5 -> Strip 5 (Trống đồng Đông Sơn)
      // Floor 6 -> Strip 2 (Lặp lại tầng 2 với cùng độ rộng 860px chuẩn)
      const isSummit = mode === 'competition' && floor >= 14 && fn === 14;
      const imgKey = isSummit ? 6 : fn === 1 && start === 1 ? 1 : 2 + ((fn - 2) % 4);

      return {
        fn,
        imgKey,
        isCurrent,
        isDone,
        isGhost,
        distance,
        src: `/assets/floors/floor-${imgKey}-cutout.webp`,
        btnY: STRIP_BTN_Y[imgKey] ?? 50,
        stripH: STRIP_H[imgKey] ?? 207,
      };
    });
  }, [floor, mode, visibleCount]);

  // Tower Scaling (fitTower) - dynamically computes screen height so tower fills 100% with ZERO gap
  const fitTower = useCallback(() => {
    const wrap = wrapRef.current;
    const stack = stackRef.current;
    if (!wrap || !stack) return;

    const wrapW = wrap.clientWidth || wrap.getBoundingClientRect().width;
    const wrapH = wrap.clientHeight || wrap.getBoundingClientRect().height;
    if (wrapW === 0 || wrapH === 0) return;

    // Tower width: on mobile we want the tower to be wide and prominent (88% - 92% of screen width, max 400px)
    const targetW = Math.min(wrapW * 0.90, 400);
    const scale = targetW / TOWER_W;
    stack.style.width = Math.floor(targetW) + 'px';

    const current = floor;
    const start = Math.max(1, current <= 3 ? 1 : current - 2);
    const getImgKey = (fn: number) => {
      const isSummit = mode === 'competition' && floor >= 14 && fn === 14;
      if (isSummit) return 6;
      if (fn === 1 && start === 1) return 1;
      return 2 + ((fn - 2) % 4);
    };

    // Calculate how many floors are needed so the tower height completely reaches or exceeds wrapH (NO GAP!)
    let accumH = 0;
    let count = 0;
    while (accumH < wrapH && count < 25) {
      const fn = start + count;
      const key = getImgKey(fn);
      accumH += (STRIP_H[key] || 207) * scale;
      count++;
    }

    // Add 1 extra buffer floor so the topmost floor extends smoothly into/past the top edge,
    // guaranteeing ZERO gap under any device resolution or high-DPI rounding!
    const neededCount = Math.max(count + 1, 6);

    if (neededCount !== visibleCount) {
      setVisibleCount(neededCount);
    }
  }, [floor, mode, visibleCount]);

  useEffect(() => {
    if (screen === 'overview') {
      fitTower();
      const wrap = wrapRef.current;
      if (!wrap) return;

      const ro = new ResizeObserver(() => {
        fitTower();
      });
      ro.observe(wrap);
      window.addEventListener('resize', fitTower);

      return () => {
        ro.disconnect();
        window.removeEventListener('resize', fitTower);
      };
    }
  }, [screen, fitTower]);

  // Advance to next floor with smooth elevation animation!
  const advanceToNextFloor = useCallback(() => {
    if (hearts === 0) {
      setGameStatus('lost');
      return;
    }

    const nextFloor = floor + 1;
    setBestFloor((b) => Math.max(b, nextFloor));

    if (floor % 7 === 0 && mode !== 'competition') {
      sfx.tap();
      setGameStatus('reward');
      return;
    }

    if (mode === 'competition' && floor >= 14) {
      setGameStatus('victory');
      sfx.win();
      try {
        confetti({ particleCount: 100, spread: 80, origin: { y: 0.6 } });
      } catch (_) {}
      return;
    }

    // Crisp iOS haptic tap
    try {
      if ('vibrate' in navigator) navigator.vibrate?.(10);
    } catch (_) {}

    sfx.tap();
    setNavDirection('forward');
    setFloor(nextFloor);
    setCurrentQuestionIdx((prev) => prev + 1);
    setGameStatus('playing');
    setScreen('play');
  }, [floor, hearts, mode]);

  // Initialize question state whenever currentQ changes
  useEffect(() => {
    setSelectedChoice(null);
    setRevealedArtwork(!currentQ.imageSpoilsAnswer);
    setIsAnswerSubmitted(false);
    setIsCorrect(false);

    // Reset matching state
    setMatchPairsMap({});
    setSelectedLeftItem(null);
    setVerifiedPairs(null);
    setMatchLines([]);

    if (currentQ.type === 'order') {
      const orderQ = currentQ as OrderQuestion;
      const cleanAnswer = orderQ.answer.replace(/\s+/g, '');
      const chars = cleanAnswer.split('').map((c, i) => ({ id: i, char: c, used: false }));
      const shuffled = [...chars].sort(() => Math.random() - 0.5);
      setScrambledLetters(shuffled);
      setPlacedLetters([]);
    }
  }, [currentQ]);

  // Restart game
  const handleRestart = () => {
    sfx.tap();
    setFloor(1);
    setHearts(3);
    setCorrectAnswers(0);
    setCurrentQuestionIdx(0);
    setGameStatus('playing');
    setScreen('overview');
  };

  // Submit multiple-choice answer
  const handleSelectChoice = (index: number) => {
    if (isAnswerSubmitted || currentQ.type !== 'choice') return;
    const choiceQ = currentQ as ChoiceQuestion;

    setSelectedChoice(index);
    setIsAnswerSubmitted(true);
    const correct = index === choiceQ.correct;
    setIsCorrect(correct);
    setRevealedArtwork(true);

    if (correct) {
      sfx.correct();
      setCorrectAnswers((prev) => prev + 1);
      try {
        confetti({ particleCount: 30, spread: 50, origin: { y: 0.7 } });
        if ('vibrate' in navigator) navigator.vibrate?.([40, 30, 40]);
      } catch (_) {}
    } else {
      sfx.wrong();
      setHearts((prev) => Math.max(0, prev - 1));
      try {
        if ('vibrate' in navigator) navigator.vibrate?.([80, 50, 80]);
      } catch (_) {}
    }
  };

  // Vua Tiếng Việt handlers
  const handlePickLetter = (item: { id: number; char: string; used: boolean }) => {
    if (isAnswerSubmitted || item.used || currentQ.type !== 'order') return;
    const orderQ = currentQ as OrderQuestion;

    sfx.tap();
    setScrambledLetters((prev) => prev.map((l) => (l.id === item.id ? { ...l, used: true } : l)));
    const newPlaced = [...placedLetters, { id: item.id, char: item.char }];
    setPlacedLetters(newPlaced);

    const cleanAnswer = orderQ.answer.replace(/\s+/g, '');
    if (newPlaced.length === cleanAnswer.length) {
      setIsAnswerSubmitted(true);
      const attemptedWord = newPlaced.map((l) => l.char).join('');
      const correct = attemptedWord === cleanAnswer;
      setIsCorrect(correct);
      setRevealedArtwork(true);

      if (correct) {
        sfx.correct();
        setCorrectAnswers((prev) => prev + 1);
        try {
          confetti({ particleCount: 30, spread: 50, origin: { y: 0.7 } });
          if ('vibrate' in navigator) navigator.vibrate?.([40, 30, 40]);
        } catch (_) {}
      } else {
        sfx.wrong();
        setHearts((prev) => Math.max(0, prev - 1));
        try {
          if ('vibrate' in navigator) navigator.vibrate?.([80, 50, 80]);
        } catch (_) {}
      }
    }
  };

  const handleRemoveLetter = (index: number) => {
    if (isAnswerSubmitted) return;
    sfx.tap();
    const removed = placedLetters[index];
    setPlacedLetters((prev) => prev.filter((_, i) => i !== index));
    setScrambledLetters((prev) => prev.map((l) => (l.id === removed.id ? { ...l, used: false } : l)));
  };

  /* ------------------ Real Interactive Matching Logic (SVG Cables) ------------------ */
  const updateMatchSvgLines = useCallback(() => {
    const container = matchContainerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();

    const newLines = Object.entries(matchPairsMap).map(([leftKey, rightKey]) => {
      const leftPort = container.querySelector(`[data-left-port="${leftKey}"]`);
      const rightPort = container.querySelector(`[data-right-port="${rightKey}"]`);
      if (!leftPort || !rightPort) return null;

      const lRect = leftPort.getBoundingClientRect();
      const rRect = rightPort.getBoundingClientRect();

      let status: 'normal' | 'correct' | 'wrong' = 'normal';
      if (verifiedPairs?.correct[leftKey]) status = 'correct';
      else if (verifiedPairs?.wrong[leftKey]) status = 'wrong';

      return {
        id: `${leftKey}-${rightKey}`,
        x1: lRect.left + lRect.width / 2 - cRect.left,
        y1: lRect.top + lRect.height / 2 - cRect.top,
        x2: rRect.left + rRect.width / 2 - cRect.left,
        y2: rRect.top + rRect.height / 2 - cRect.top,
        status,
      };
    }).filter(Boolean) as {
      id: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      status: 'normal' | 'correct' | 'wrong';
    }[];

    setMatchLines(newLines);
  }, [matchPairsMap, verifiedPairs]);

  useEffect(() => {
    updateMatchSvgLines();
    window.addEventListener('resize', updateMatchSvgLines);
    return () => window.removeEventListener('resize', updateMatchSvgLines);
  }, [updateMatchSvgLines]);

  const handleMatchLeftClick = (leftTitle: string) => {
    if (isAnswerSubmitted) return;
    sfx.tap();

    // If currently selected, deselect
    if (selectedLeftItem === leftTitle) {
      setSelectedLeftItem(null);
      return;
    }

    // If this left already has a link, clicking it unlinks it so user can reconnect
    if (matchPairsMap[leftTitle]) {
      setMatchPairsMap((prev) => {
        const next = { ...prev };
        delete next[leftTitle];
        return next;
      });
      setSelectedLeftItem(leftTitle);
      return;
    }

    setSelectedLeftItem(leftTitle);
  };

  const handleMatchRightClick = (rightVal: string) => {
    if (isAnswerSubmitted) return;
    sfx.tap();

    if (!selectedLeftItem) {
      // If no left item is selected, check if this right item is already connected to any left item
      const linkedLeft = Object.keys(matchPairsMap).find((k) => matchPairsMap[k] === rightVal);
      if (linkedLeft) {
        setMatchPairsMap((prev) => {
          const next = { ...prev };
          delete next[linkedLeft];
          return next;
        });
        setSelectedLeftItem(linkedLeft);
      }
      return;
    }

    // Connect selectedLeftItem with this right item
    setMatchPairsMap((prev) => {
      const next = { ...prev };
      // If any other left item was linked to this right item, disconnect it
      Object.keys(next).forEach((k) => {
        if (next[k] === rightVal) delete next[k];
      });
      next[selectedLeftItem] = rightVal;
      return next;
    });

    setSelectedLeftItem(null);
  };

  // Validate all connected pairs
  const handleVerifyMatching = () => {
    if (isAnswerSubmitted || currentQ.type !== 'match') return;
    const matchQ = currentQ as MatchQuestion;
    if (Object.keys(matchPairsMap).length < matchQ.pairs.length) return;

    let allOk = true;
    const newCorrect: Record<string, boolean> = {};
    const newWrong: Record<string, boolean> = {};

    matchQ.pairs.forEach((p) => {
      if (matchPairsMap[p.left] === p.right) {
        newCorrect[p.left] = true;
      } else {
        allOk = false;
        newWrong[p.left] = true;
      }
    });

    setVerifiedPairs({ correct: newCorrect, wrong: newWrong });

    if (allOk) {
      sfx.correct();
      setIsAnswerSubmitted(true);
      setIsCorrect(true);
      setRevealedArtwork(true);
      setCorrectAnswers((prev) => prev + 1);
      try {
        confetti({ particleCount: 35, spread: 60, origin: { y: 0.7 } });
      } catch (_) {}
    } else {
      sfx.wrong();
      setHearts((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) {
          setIsAnswerSubmitted(true);
          setIsCorrect(false);
          setRevealedArtwork(true);
        }
        return next;
      });

      // After 1 second, disconnect wrong pairs so user can re-wire
      setTimeout(() => {
        setMatchPairsMap((prev) => {
          const next = { ...prev };
          Object.keys(newWrong).forEach((k) => delete next[k]);
          return next;
        });
        setVerifiedPairs(null);
      }, 1200);
    }
  };

  // Spin Lucky Wheel (P1.2)
  const handleSpinWheel = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setPrizeWon(null);

    sfx.spin();
    try {
      if ('vibrate' in navigator) navigator.vibrate?.([30, 40, 30, 40, 30]);
    } catch (_) {}

    const targetIdx = Math.floor(Math.random() * LUCKY_WHEEL_PRIZES.length);
    const prize = LUCKY_WHEEL_PRIZES[targetIdx];

    const totalAngle = spinCount * 1800 + (360 - (45 + targetIdx * 90));
    setWheelRotation(totalAngle);
    setSpinCount((c) => c + 1);

    setTimeout(() => {
      setIsSpinning(false);
      setPrizeWon(prize);

      if (prize.hearts > 0) {
        setHearts((prev) => Math.min(3, prev + prize.hearts));
      }
      if (prize.isStamp) {
        setInventory((prev) => (prev.some((p) => p.id === prize.id) ? prev : [...prev, prize]));
      }

      sfx.win();
      try {
        if ('vibrate' in navigator) navigator.vibrate?.([60, 40, 60, 40, 100]);
        confetti({
          particleCount: 85,
          spread: 75,
          origin: { y: 0.6 },
        });
      } catch (_) {}
    }, 4000);
  };

  /* Render HUD Hearts */
  const renderHearts = (lives: number) => {
    return [1, 2, 3].map((h) => (
      <Heart
        key={h}
        className={`w-3.5 h-3.5 transition-all duration-300 ${
          h <= lives
            ? 'text-[#e53935] fill-[#e53935] drop-shadow-[0_0_4px_rgba(229,57,53,0.7)]'
            : 'text-[#5a484e] fill-[#38262d]'
        }`}
      />
    ));
  };

  const spinPer = 7;
  const doneSince = (floor - 1) % spinPer;

  return (
    <div className="w-full h-full min-h-screen-dvh flex flex-col bg-[#1c1217] text-[#fff4dd] relative overflow-hidden select-none font-sans">
      {/* ============================================================
          MÀN HÌNH 1: BẢN ĐỒ THÁP (TOWER OVERVIEW MAP)
          ============================================================ */}
      {screen === 'overview' && (
        <div className={`flex-1 flex flex-col justify-between w-full max-w-md mx-auto h-full p-2.5 sm:p-3 overflow-hidden paper-bg relative z-10 select-none font-sans ${navDirection === 'back' ? 'ios-page-pop' : ''}`}>
          {/* Top HUD - Refined VNFAM Museum Header */}
          <header className="flex items-center justify-between gap-1.5 px-1 pt-1 pb-1 relative z-20 shrink-0 border-b border-[#e6ba55]/20 bg-gradient-to-b from-[#1c1217]/90 to-transparent backdrop-blur-sm">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2a1720]/85 hover:bg-[#3d222e] text-[#f2d18d] border border-[#e6ba55]/40 text-xs font-serif font-bold transition-all shadow-md active:scale-95 cursor-pointer"
              aria-label="Về trang chủ"
              title="Về trang chủ"
            >
              <ChevronLeft className="w-4 h-4 text-[#e6ba55]" />
              <span>VNFAM</span>
            </button>

            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2a1720]/85 border border-[#e6ba55]/40 shadow-md">
              <span className="text-xs font-serif font-bold tracking-wider text-[#ffe4a0]">
                TẦNG {String(floor).padStart(2, '0')}
              </span>
              {mode === 'competition' && (
                <span className="text-[9px] bg-[#a50021] text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                  Thi tuần
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#2a1720]/85 border border-[#e6ba55]/40 shadow-md">
              {renderHearts(hearts)}
            </div>

            {/* Nút BXH */}
            <button
              onClick={onOpenLeaderboard}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#2a1720]/85 hover:bg-[#3d222e] text-[#f2d18d] border border-[#e6ba55]/40 text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer"
              title="Xem bảng xếp hạng"
            >
              <Trophy className="w-3.5 h-3.5 text-[#e6ba55]" />
              <span className="font-serif">T.{bestFloor}</span>
            </button>
          </header>

          {/* Floating Backpack (Túi vật phẩm) - Right side FAB */}
          <button
            id="btn-inventory"
            onClick={() => {
              sfx.tap();
              setShowInventoryModal(true);
            }}
            className="absolute right-3 sm:right-4 top-28 z-30 flex flex-col items-center gap-1 group cursor-pointer active:scale-90 transition-all duration-200"
            title="Túi vật phẩm"
            aria-label="Túi vật phẩm"
          >
            <div className="relative w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-b from-[#2d1822]/95 via-[#23121b]/95 to-[#190b13]/95 border-2 border-[#e6ba55]/60 group-hover:border-[#e6ba55] shadow-[0_4px_16px_rgba(0,0,0,0.6),0_0_12px_rgba(230,186,85,0.25)] flex items-center justify-center backdrop-blur-md transition-all group-hover:scale-105">
              <img
                src="/assets/item-bag.webp"
                alt="Túi vật phẩm"
                className="w-6 h-6 object-contain drop-shadow"
                draggable={false}
              />
              {inventory.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#a50021] text-[#fff4dd] border-2 border-[#e6ba55] text-[9px] font-bold flex items-center justify-center shadow-md animate-pulse">
                  {inventory.length}
                </span>
              )}
            </div>
            <span className="text-[9px] font-serif font-bold text-[#f2d18d] bg-[#1a0c14]/90 px-2 py-0.5 rounded-full border border-[#e6ba55]/40 shadow-xs tracking-wider">
              Túi đồ
            </span>
          </button>

          {/* Heading */}
          <div className="home-heading text-center relative z-10 px-3 py-1.5 shrink-0">
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#2a1720]/85 border border-[#e6ba55]/35 text-[10px] tracking-wider text-[#f2d18d] font-semibold shadow-xs">
              <span className="text-[#e6ba55]">✨</span>
              <span>{mode === 'competition' ? 'ĐẤU TRƯỜNG THI TUẦN' : 'THÁP VÔ HẠN DI SẢN'}</span>
            </div>
            <h1 className="text-base sm:text-lg font-serif font-bold tracking-tight text-[#fff4dd] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] mt-0.5 leading-tight">
              Leo tháp <em>kiệt tác</em>
            </h1>
            <p className="text-[10px] sm:text-[11px] text-[#fff1dccc] font-sans">
              Chinh phục từng tầng di sản · Mở khóa bảo vật quốc gia
            </p>
          </div>

          {/* Tower Wrap with Auto Fit */}
          <div className="tower-wrap flex-1 min-h-0 relative w-full flex items-end justify-center overflow-hidden" id="tower-wrap" ref={wrapRef}>
            {/* Top mist / gradient transition */}
            <div className="pointer-events-none absolute top-0 inset-x-0 h-10 bg-gradient-to-b from-[#1c1217] via-[#1c1217]/50 to-transparent z-20" />

            {/* Clouds */}
            <img src="/assets/cloud-1.webp" alt="" className="cloud-deco cloud-a pointer-events-none select-none" />
            <img src="/assets/cloud-2.webp" alt="" className="cloud-deco cloud-b pointer-events-none select-none" />
            <img src="/assets/cloud-1.webp" alt="" className="cloud-deco cloud-c pointer-events-none select-none" />
            <img src="/assets/cloud-2.webp" alt="" className="cloud-deco cloud-d pointer-events-none select-none" />

            {/* Tower Stack of Cutout Strips */}
            <div id="tower-stack" className="tower-stack relative flex flex-col items-center z-10" ref={stackRef}>
              {towerStrips.map((strip) => {
                return (
                  <div
                    key={strip.fn}
                    className={`tower-strip relative w-full leading-none ${strip.isCurrent ? 'current' : ''}`}
                    style={{
                      opacity: strip.isGhost ? Math.max(0.4, 0.9 - strip.distance * 0.12) : 1,
                      pointerEvents: strip.isGhost ? 'none' : 'auto',
                    }}
                  >
                    <img
                      src={strip.src}
                      alt={`Tầng ${strip.fn}`}
                      className="w-full block select-none pointer-events-none"
                      draggable={false}
                      onLoad={() => requestAnimationFrame(fitTower)}
                    />
                    <button
                      type="button"
                      className={`floor-dot ${strip.isCurrent ? 'current' : strip.isDone ? 'done' : 'ghost'}`}
                      style={{ top: `${strip.btnY}%` }}
                      onClick={() => {
                        if (!strip.isGhost) {
                          sfx.tap();
                          setNavDirection('forward');
                          setScreen('play');
                        }
                      }}
                      disabled={strip.isGhost}
                      title={`Tầng ${strip.fn}`}
                    >
                      {strip.isDone ? '✓' : strip.fn}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Tower Mist overlay at top */}
            <div className="tower-mist pointer-events-none"></div>
          </div>

          {/* Bottom Controls Rail */}
          <div className="shrink-0 w-full px-1 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom,8px))] space-y-1 relative z-20">
            {/* Pips vòng quay chỉ hiện ở mode thường */}
            {mode !== 'competition' && (
              <div className="flex items-center justify-center gap-2 py-0.5 px-3 rounded-full bg-[#25151e]/85 border border-[#e6ba55]/30 max-w-sm mx-auto">
                <span className="text-[9px] font-bold tracking-wider text-[#e6ba55] uppercase">
                  VÒNG QUAY BẢO VẬT:
                </span>
                <div className="flex items-center gap-1">
                  {Array.from({ length: spinPer }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        i < doneSince
                          ? 'bg-[#e6ba55] shadow-[0_0_6px_rgba(230,186,85,0.8)] scale-110'
                          : 'bg-[#38222b] border border-[#7d5635]'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-[9px] text-[#cdbdaf] font-semibold font-serif">
                  {doneSince === 0
                    ? `Mốc tầng ${floor}`
                    : `Còn ${spinPer - doneSince} tầng`}
                </span>
              </div>
            )}

            {/* CTA leo tháp */}
            <button
              id="btn-continue"
              className="w-full max-w-sm mx-auto min-h-[44px] px-5 py-2 rounded-xl bg-gradient-to-r from-[#b91c1c] via-[#a50021] to-[#790019] text-[#fff4dd] border-2 border-[#e6ba55] shadow-[0_0_20px_rgba(230,186,85,0.35),0_6px_20px_rgba(0,0,0,0.6)] font-serif font-bold text-sm sm:text-base tracking-wide flex items-center justify-between transition-all duration-200 hover:scale-[1.01] active:scale-[0.98] cursor-pointer"
              onClick={() => {
                sfx.tap();
                setNavDirection('forward');
                setScreen('play');
              }}
            >
              <span className="flex items-center gap-2">
                <span className="text-base text-[#f4ce80]">✦</span>
                <span>TIẾP TỤC LEO THÁP</span>
              </span>
              <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#24171d]/80 border border-[#e6ba55]/60 text-xs text-[#ffe4a0]">
                <span>TẦNG {floor}</span>
                <span className="text-[#e6ba55]">↗</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ============================================================
          MÀN HÌNH 2: TRẢ LỜI CÂU HỎI (QUIZ SCREEN WITH REAL MATCHING & SOFT BLUR)
          ============================================================ */}
      {screen === 'play' && gameStatus === 'playing' && (
        <div className={`flex-1 flex flex-col w-full h-full relative overflow-hidden ${navDirection === 'forward' ? 'ios-page-push' : 'ios-page-pop'}`}>
          {/* Authentic Vietnamese Pagoda Tower Interior Background - Soft, Atmospheric & Non-intrusive */}
          <div className="absolute inset-0 pointer-events-none select-none z-0 overflow-hidden">
            <img
              src="/assets/bg-tower-interior.webp"
              alt=""
              className="w-full h-full object-cover object-center scale-105 filter blur-[3.5px] brightness-[0.58] contrast-[0.92] saturate-[0.85]"
              aria-hidden="true"
            />
            {/* Atmospheric lacquer wash & vertical vignette to ensure foreground content remains the absolute hero */}
            <div className="absolute inset-0 bg-[#160c12]/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#180e15]/85 via-transparent to-[#12080d]/85" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,_transparent_40%,_rgba(18,8,13,0.5)_100%)]" />
          </div>

          {/* Centered content column */}
          <div className="flex-1 flex flex-col max-w-xl mx-auto w-full px-3.5 sm:px-4 pt-[env(safe-area-inset-top,4px)] min-h-0 relative z-10">
            {/* Header Compact (Chỉ Chevron Back + Tầng + Tim) */}
            <div className="h-12 flex items-center justify-between gap-3 border-b border-[#e6ba55]/25 shrink-0 relative z-10">
              <button
                onClick={() => {
                  sfx.tap();
                  setNavDirection('back');
                  setScreen('overview');
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#24151e]/90 hover:bg-[#361e2e] text-[#f2d18d] border border-[#e6ba55]/40 text-xs font-bold transition-all active:scale-90 shadow-md cursor-pointer"
                aria-label="Quay lại tổng quan tháp"
                title="Quay lại tổng quan tháp"
              >
                <ChevronLeft className="w-4 h-4 text-[#e6ba55]" />
                <span>Tháp</span>
              </button>

              <div className="flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#24151e]/90 border border-[#e6ba55]/40 text-white shadow-md">
                <span className="text-xs font-serif font-bold tracking-wider text-[#ffe4a0]">
                  TẦNG {String(floor).padStart(2, '0')}
                </span>
                {mode === 'competition' && (
                  <span className="text-[9px] bg-[#a50021] text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                    Thi tuần
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1 bg-[#24151e]/90 px-2.5 py-1.5 rounded-full border border-[#e6ba55]/40 shadow-md">
                {[1, 2, 3].map((h) => (
                  <Heart
                    key={h}
                    className={`w-3.5 h-3.5 transition-all duration-300 ${
                      h <= hearts
                        ? 'text-[#e53935] fill-[#e53935] drop-shadow-[0_0_4px_rgba(229,57,53,0.6)]'
                        : 'text-[#5a484e] fill-[#38262d]'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Question card (Scrollable body) */}
            <div key={floor} className="flex-1 overflow-y-auto py-3 space-y-3 min-h-0 ios-page-push relative z-10">
              {/* Question Text in Refined Lacquer Card */}
              <div className="text-center p-3.5 rounded-2xl bg-[#22131b]/92 border border-[#e6ba55]/30 shadow-lg">
                <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#180d14] border border-[#e6ba55]/35 text-[10px] font-bold tracking-widest text-[#e6ba55] uppercase shadow-xs">
                  {currentQ.label}
                </span>
                <h2 className="text-base sm:text-lg font-serif font-bold text-[#fff4dd] mt-1.5 leading-snug">
                  {currentQ.text}
                </h2>
              </div>

            {/* ARTWORK DISPLAY FOR CHOICE & ORDER (GENTLE BLUR [5px] - NOT MILKY) */}
            {currentQ.type !== 'match' && currentQ.imageUrl && (
              <div className="relative w-full max-w-sm mx-auto h-44 sm:h-52 rounded-2xl overflow-hidden border border-[#e6ba55]/40 bg-black/35 backdrop-blur-xs shadow-2xl group">
                <img
                  src={currentQ.imageUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-xl opacity-35 scale-125 select-none pointer-events-none"
                  aria-hidden="true"
                />

                {/* Main Artwork: Soft blur-[5px] so artwork is tantalizingly visible, transitions smoothly to crisp blur-0 */}
                <img
                  src={currentQ.imageUrl}
                  alt={currentQ.imageAlt || currentQ.imageCaption || 'Tác phẩm mỹ thuật'}
                  className={`relative z-10 w-full h-full object-contain transition-all duration-500 select-none ${
                    !revealedArtwork ? 'blur-[5px] scale-102 opacity-95' : 'blur-0 scale-100 cursor-pointer opacity-100'
                  }`}
                  onClick={() => {
                    if (revealedArtwork && currentQ.imageUrl) {
                      setZoomImage(currentQ.imageUrl);
                    }
                  }}
                />

                {/* Glassmorphic Badge when hidden */}
                {!revealedArtwork && (
                  <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-[#1b1015]/90 border border-[#e6ba55]/60 text-[#f3d99e] text-[10px] font-bold shadow-lg backdrop-blur-md flex items-center gap-1.5 pointer-events-none whitespace-nowrap animate-pulse">
                    <EyeOff className="w-3.5 h-3.5 text-[#e6ba55]" />
                    <span>Bảo vật ẩn đáp án · Chọn để mở khóa</span>
                  </div>
                )}

                {/* Zoom button top-right when revealed */}
                {revealedArtwork && (
                  <button
                    onClick={() => currentQ.imageUrl && setZoomImage(currentQ.imageUrl)}
                    className="absolute top-2.5 right-2.5 z-20 p-1.5 rounded-full bg-[#261820]/90 border border-[#8e7148]/80 text-[#e6ba55] hover:bg-[#3b2631] hover:scale-105 active:scale-95 transition-all shadow-md backdrop-blur-sm"
                    title="Phóng to chi tiết tác phẩm"
                    aria-label="Phóng to chi tiết tác phẩm"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                )}

                {/* Caption Bar bottom: revealed after answer */}
                {revealedArtwork && currentQ.imageCaption && (
                  <div className="absolute bottom-0 inset-x-0 z-20 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-3 pb-1.5 px-3 text-[#fff4dd] text-[11px] text-center font-medium border-t border-white/10 animate-fade-in flex items-center justify-center gap-1.5">
                    <span className="text-[#e6ba55] text-xs">✨</span>
                    <span className="drop-shadow truncate">{currentQ.imageCaption}</span>
                  </div>
                )}
              </div>
            )}

            {/* CHOICE QUESTIONS (TRẮC NGHIỆM) */}
            {currentQ.type === 'choice' && (
              <div className="grid grid-cols-1 gap-2 pt-1">
                {(currentQ.choices || currentQ.options || []).map((opt, i) => {
                  const isSelected = i === selectedChoice;
                  const isTheCorrectOne = i === currentQ.correct;

                  let btnStyle = 'bg-[#24151e]/95 border border-[#7d6141]/80 text-[#fff4dd] hover:bg-[#381f2f] hover:border-[#e6ba55] shadow-md';

                  if (isAnswerSubmitted) {
                    if (isTheCorrectOne) {
                      btnStyle =
                        'bg-[#143a29] border-2 border-[#4caf82] text-[#e8fbf2] font-bold shadow-[0_0_15px_rgba(76,175,130,0.4)]';
                    } else if (isSelected) {
                      btnStyle =
                        'bg-[#481a22] border-2 border-[#ef5350] text-[#ffebee] font-semibold';
                    } else {
                      btnStyle =
                        'bg-[#20151a]/50 border-[#3d2c33]/60 text-[#7a6a70] opacity-40';
                    }
                  }

                  return (
                    <button
                      key={i}
                      disabled={isAnswerSubmitted}
                      onClick={() => handleSelectChoice(i)}
                      className={`w-full text-left p-3 rounded-xl border transition-all flex items-center justify-between text-xs sm:text-sm active:scale-[0.98] cursor-pointer ${btnStyle}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 pr-2">
                        <span
                          className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 border ${
                            isAnswerSubmitted && isTheCorrectOne
                              ? 'bg-[#4caf82] text-[#0a2016] border-[#4caf82]'
                              : isAnswerSubmitted && isSelected
                              ? 'bg-[#ef5350] text-white border-[#ef5350]'
                              : 'bg-[#1b1016]/80 border-[#e6ba55]/40 text-[#f2d18d]'
                          }`}
                        >
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className="truncate">{opt}</span>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {isAnswerSubmitted && isTheCorrectOne && (
                          <span className="text-[10px] uppercase font-bold tracking-wider text-[#6fe3aa] bg-[#0c261b] px-2 py-0.5 rounded border border-[#4caf82]/50 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" />
                            Đáp án đúng
                          </span>
                        )}
                        {isAnswerSubmitted && isSelected && !isTheCorrectOne && (
                          <span className="text-[10px] uppercase font-bold tracking-wider text-[#ffa4a2] bg-[#2e1015] px-2 py-0.5 rounded border border-[#ef5350]/50 flex items-center gap-1">
                            <XIcon className="w-3.5 h-3.5" />
                            Bạn chọn
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ORDER QUESTIONS (VUA TIẾNG VIỆT) */}
            {currentQ.type === 'order' && (
              <div className="space-y-3 pt-1">
                {/* Placed letters target container */}
                <div
                  className={`min-h-[52px] p-2.5 rounded-xl border-2 transition-all flex flex-wrap gap-1.5 items-center justify-center ${
                    isAnswerSubmitted
                      ? isCorrect
                        ? 'border-[#4caf82] bg-[#143a29]/40'
                        : 'border-[#ef5350] bg-[#481a22]/40'
                      : 'border-dashed border-[#8e7148]/60 bg-black/20'
                  }`}
                >
                  {placedLetters.length === 0 ? (
                    <span className="text-xs text-[#cdbdaf]">
                      Chạm các chữ cái bên dưới để ghép từ hoàn chỉnh
                    </span>
                  ) : (
                    placedLetters.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleRemoveLetter(idx)}
                        disabled={isAnswerSubmitted}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg font-bold text-sm shadow flex items-center justify-center border transition-all ${
                          isAnswerSubmitted
                            ? isCorrect
                              ? 'bg-[#1b4332] text-[#d4f7e7] border-[#4caf82]'
                              : 'bg-[#5c1c28] text-[#ffcdd2] border-[#ef5350]'
                            : 'bg-[#a50021] text-white border-[#e6ba55]/60 hover:scale-105 active:scale-95'
                        }`}
                      >
                        {item.char}
                      </button>
                    ))
                  )}
                </div>

                {/* Scrambled candidate letters */}
                {!isAnswerSubmitted && (
                  <div className="flex flex-wrap gap-2 justify-center pt-1">
                    {scrambledLetters.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handlePickLetter(item)}
                        disabled={item.used || isAnswerSubmitted}
                        className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl font-bold text-sm sm:text-base transition-all shadow ${
                          item.used
                            ? 'bg-[#281a21]/40 text-transparent border border-transparent cursor-not-allowed opacity-20'
                            : 'bg-[#22131b]/85 backdrop-blur-md text-[#fff4dd] border border-[#e6ba55]/40 hover:bg-[#381c2b] active:scale-95 cursor-pointer'
                        }`}
                      >
                        {item.char}
                      </button>
                    ))}
                  </div>
                )}

                {/* If incorrect in Order mode, clearly reveal the correct word */}
                {isAnswerSubmitted && !isCorrect && (
                  <div className="p-3 rounded-xl bg-[#143324] border border-[#3fa37c] text-center shadow-md animate-fade-in">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-[#7ce0b2] block mb-0.5">
                      Đáp án chính xác:
                    </span>
                    <span className="text-base font-serif font-black text-[#ffffff] tracking-widest">
                      {(currentQ as OrderQuestion).answer}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ============================================================
                REAL INTERACTIVE MATCHING (2-COLUMN VISUAL SVG CABLES)
                ============================================================ */}
            {currentQ.type === 'match' && (() => {
              const matchQ = currentQ as MatchQuestion;
              const allConnected = Object.keys(matchPairsMap).length === matchQ.pairs.length;

              return (
                <div className="space-y-2 pt-1 max-w-md mx-auto w-full">
                  <div className="text-center pb-1">
                    <span className="text-[11px] font-bold text-[#e6ba55] tracking-wide uppercase flex items-center justify-center gap-1.5">
                      <span>🔗</span>
                      <span>Chạm tác phẩm bên trái ➔ Chạm tác giả/thể loại bên phải để nối</span>
                    </span>
                  </div>

                  {/* Visual 2-Column Matching Stage with SVG Connection Cables */}
                  <div ref={matchContainerRef} className="relative py-2 px-1 select-none">
                    {/* SVG Canvas for dynamic connection cords */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                      <defs>
                        <filter id="goldGlow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#e6ba55" floodOpacity="0.8" />
                        </filter>
                        <filter id="greenGlow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#4caf82" floodOpacity="0.9" />
                        </filter>
                        <filter id="redGlow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#ef5350" floodOpacity="0.9" />
                        </filter>
                      </defs>

                      {matchLines.map((line) => {
                        const dx = Math.abs(line.x2 - line.x1) * 0.55;
                        const d = `M ${line.x1} ${line.y1} C ${line.x1 + dx} ${line.y1}, ${line.x2 - dx} ${line.y2}, ${line.x2} ${line.y2}`;

                        let strokeColor = '#e6ba55';
                        let filterId = 'url(#goldGlow)';
                        let strokeWidth = 3;
                        let strokeDash = 'none';

                        if (line.status === 'correct') {
                          strokeColor = '#4caf82';
                          filterId = 'url(#greenGlow)';
                          strokeWidth = 3.5;
                        } else if (line.status === 'wrong') {
                          strokeColor = '#ef5350';
                          filterId = 'url(#redGlow)';
                          strokeWidth = 3.5;
                          strokeDash = '6 4';
                        }

                        return (
                          <g key={line.id}>
                            <path
                              d={d}
                              fill="none"
                              stroke={strokeColor}
                              strokeWidth={strokeWidth}
                              strokeDasharray={strokeDash}
                              filter={filterId}
                              strokeLinecap="round"
                            />
                            {/* Animated flow dot for pending connection */}
                            {line.status === 'normal' && (
                              <circle r="3.5" fill="#fff4dd">
                                <animateMotion dur="2s" repeatCount="indefinite" path={d} />
                              </circle>
                            )}
                          </g>
                        );
                      })}
                    </svg>

                    {/* Left and Right Columns */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 relative z-20">
                      {/* Left Column: Artworks with thumbnails & Right Ports */}
                      <div className="flex flex-col gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#cdbdaf] text-center">
                          Tác phẩm
                        </span>
                        {matchQ.pairs.map((p) => {
                          const isSelected = selectedLeftItem === p.left;
                          const isConnected = !!matchPairsMap[p.left];
                          const isCorrect = verifiedPairs?.correct[p.left];
                          const isWrong = verifiedPairs?.wrong[p.left];

                          return (
                            <div
                              key={p.left}
                              onClick={() => handleMatchLeftClick(p.left)}
                              className={`relative p-2 rounded-xl border transition-all duration-200 flex items-center justify-between cursor-pointer active:scale-98 ${
                                isCorrect
                                  ? 'bg-[#143a29]/95 border-[#4caf82] shadow-[0_0_12px_rgba(76,175,130,0.5)] backdrop-blur-md'
                                  : isWrong
                                  ? 'bg-[#4a1a24]/95 border-[#ef5350] shadow-[0_0_12px_rgba(239,83,80,0.5)] backdrop-blur-md'
                                  : isSelected
                                  ? 'bg-[#3b2531]/95 border-[#e6ba55] shadow-[0_0_12px_rgba(230,186,85,0.6)] ring-2 ring-[#e6ba55]/60 backdrop-blur-md'
                                  : isConnected
                                  ? 'bg-[#281a23]/90 border-[#d4a24e]/80 shadow-md backdrop-blur-md'
                                  : 'bg-[#201018]/85 backdrop-blur-md border border-[#e6ba55]/30 hover:border-[#e6ba55]/70 shadow-md'
                              }`}
                            >
                              <div className="flex items-center gap-2 min-w-0 pr-1">
                                {p.image && (
                                  <div
                                    className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-[#8e7148]/60 bg-black/40 relative cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (p.image) setZoomImage(p.image);
                                    }}
                                    title="Chạm để phóng to"
                                  >
                                    <img src={p.image} alt={p.left} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/20 hover:bg-transparent flex items-center justify-center">
                                      <ZoomIn className="w-3 h-3 text-white/80" />
                                    </div>
                                  </div>
                                )}
                                <span className="text-xs font-serif font-bold text-[#ffe4a0] leading-tight truncate">
                                  {p.left}
                                </span>
                              </div>

                              {/* Connection Port Anchor Right */}
                              <div
                                data-left-port={p.left}
                                className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-all ${
                                  isCorrect
                                    ? 'bg-[#4caf82] border-[#4caf82] shadow-[0_0_8px_#4caf82]'
                                    : isWrong
                                    ? 'bg-[#ef5350] border-[#ef5350] shadow-[0_0_8px_#ef5350]'
                                    : isSelected || isConnected
                                    ? 'bg-[#e6ba55] border-[#fff4dd] shadow-[0_0_8px_#e6ba55]'
                                    : 'bg-[#1b1116] border-[#8e7148]'
                                }`}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* Right Column: Authors / Genres with Left Ports */}
                      <div className="flex flex-col gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#cdbdaf] text-center">
                          {matchQ.rightTitle || 'Tác giả / Thể loại'}
                        </span>
                        {shuffledMatchRights.map((rVal) => {
                          const connectedLeft = Object.keys(matchPairsMap).find(
                            (k) => matchPairsMap[k] === rVal
                          );
                          const isConnected = !!connectedLeft;
                          const isCorrect = connectedLeft ? verifiedPairs?.correct[connectedLeft] : false;
                          const isWrong = connectedLeft ? verifiedPairs?.wrong[connectedLeft] : false;

                          return (
                            <div
                              key={rVal}
                              onClick={() => handleMatchRightClick(rVal)}
                              className={`relative p-2.5 rounded-xl border transition-all duration-200 flex items-center gap-2 cursor-pointer active:scale-98 min-h-[58px] ${
                                isCorrect
                                  ? 'bg-[#143a29]/95 border-[#4caf82] shadow-[0_0_12px_rgba(76,175,130,0.5)] backdrop-blur-md'
                                  : isWrong
                                  ? 'bg-[#4a1a24]/95 border-[#ef5350] shadow-[0_0_12px_rgba(239,83,80,0.5)] backdrop-blur-md'
                                  : isConnected
                                  ? 'bg-[#281a23]/90 border-[#d4a24e]/80 shadow-md backdrop-blur-md'
                                  : 'bg-[#201018]/85 backdrop-blur-md border border-[#e6ba55]/30 hover:border-[#e6ba55]/70 shadow-md'
                              }`}
                            >
                              {/* Connection Port Anchor Left */}
                              <div
                                data-right-port={rVal}
                                className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-all ${
                                  isCorrect
                                    ? 'bg-[#4caf82] border-[#4caf82] shadow-[0_0_8px_#4caf82]'
                                    : isWrong
                                    ? 'bg-[#ef5350] border-[#ef5350] shadow-[0_0_8px_#ef5350]'
                                    : isConnected
                                    ? 'bg-[#e6ba55] border-[#fff4dd] shadow-[0_0_8px_#e6ba55]'
                                    : 'bg-[#1b1116] border-[#8e7148]'
                                }`}
                              />

                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-semibold text-[#fff4dd] leading-snug block truncate">
                                  {rVal}
                                </span>
                                {connectedLeft && (
                                  <span className="text-[9px] text-[#e6ba55] font-medium block truncate mt-0.5">
                                     nối: {connectedLeft}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Verify Matching Button */}
                  {!isAnswerSubmitted && (
                    <button
                      onClick={handleVerifyMatching}
                      disabled={!allConnected}
                      className={`w-full py-2.5 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all shadow-md ${
                        allConnected
                          ? 'gf-button gf-button-gold animate-bounce shadow-[0_0_15px_rgba(230,186,85,0.5)]'
                          : 'bg-[#251820] border border-[#7d6141]/40 text-[#7a6a70] cursor-not-allowed opacity-60'
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>{allConnected ? 'KIỂM TRA GHÉP NỐI ➔' : `Hãy nối đủ ${matchQ.pairs.length} cặp để kiểm tra`}</span>
                    </button>
                  )}
                </div>
              );
            })()}

            {/* STOP & LEARN FEEDBACK & EXPLANATION CARD */}
            {isAnswerSubmitted && (
              <div className="p-3.5 rounded-2xl bg-[#1c0f17]/90 backdrop-blur-md border border-[#e6ba55]/40 shadow-xl space-y-2 animate-fade-in">
                <div className="flex items-center justify-between gap-2 border-b border-[#5a4249]/60 pb-2">
                  <div className="flex items-center gap-2">
                    {isCorrect ? (
                      <div className="w-6 h-6 rounded-full bg-[#1b4332] text-[#4caf82] border border-[#4caf82] flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#4a1c24] text-[#ef5350] border border-[#ef5350] flex items-center justify-center">
                        <AlertCircle className="w-4 h-4" />
                      </div>
                    )}
                    <span
                      className={`text-xs sm:text-sm font-serif font-bold ${
                        isCorrect ? 'text-[#6fe3aa]' : 'text-[#ff8a80]'
                      }`}
                    >
                      {isCorrect ? 'Chính xác! Bạn đã ghi điểm.' : 'Chưa chính xác!'}
                    </span>
                  </div>

                  <span className="text-[10px] font-bold text-[#e6ba55] bg-[#3a2731] px-2.5 py-1 rounded-full border border-[#8e7148]/40 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3 text-[#e6ba55]" />
                    Kiến thức mỹ thuật
                  </span>
                </div>

                {currentQ.explanation && (
                  <p className="text-xs sm:text-[13px] leading-relaxed text-[#f3e5d0]">
                    {currentQ.explanation}
                  </p>
                )}

                {/* For matching question when wrong, show correct pairs recap */}
                {currentQ.type === 'match' && !isCorrect && (
                  <div className="p-2.5 rounded-xl bg-[#143324] border border-[#3fa37c] shadow-md space-y-1 mt-1.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-[#7ce0b2] block mb-0.5">
                      Đáp án chính xác các cặp:
                    </span>
                    {(currentQ as MatchQuestion).pairs.map((p) => (
                      <div key={p.left} className="text-xs text-white flex items-center justify-between py-0.5 border-b border-white/5 last:border-0">
                        <span className="text-[#ffe4a0] font-serif font-bold">{p.left}</span>
                        <span className="text-[#7ce0b2]">→ {p.right}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* BOTTOM ACTION BUTTON (MANUAL PROGRESSION AT USER PACE) */}
          {isAnswerSubmitted && (
            <div className="pt-2.5 pb-[max(1rem,env(safe-area-inset-bottom,16px))] shrink-0 sticky bottom-0 bg-gradient-to-t from-[#1c1217] via-[#1c1217]/98 to-transparent z-20 space-y-1.5">
              {hearts > 0 ? (
                <div>
                  <button
                    onClick={advanceToNextFloor}
                    className="w-full gf-button gf-button-gold text-sm sm:text-base py-3 flex items-center justify-center gap-2 shadow-2xl active:scale-[0.98] font-bold cursor-pointer"
                  >
                    <span>LÊN TẦNG {floor + 1} TIẾP THEO</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={advanceToNextFloor}
                  className="w-full gf-button bg-[#a50021] text-white hover:bg-[#88001b] text-sm sm:text-base py-3.5 flex items-center justify-center gap-2 shadow-2xl active:scale-[0.98] font-bold border border-[#e6ba55]/60 cursor-pointer"
                >
                  <RotateCcw className="w-5 h-5" />
                  <span>HẾT TIM · XEM TỔNG KẾT</span>
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      )}

      {/* ============================================================
          TẦNG THƯỞNG: VÒNG QUAY MAY MẮN (LUCKY WHEEL)
          ============================================================ */}
      {gameStatus === 'reward' && (
        <div className="flex-1 flex flex-col items-center justify-center p-4 max-w-md mx-auto w-full animate-fade-in text-center pb-[max(1rem,env(safe-area-inset-bottom,16px))] bg-[#1c1217]">
          <span className="text-xs font-bold uppercase tracking-widest text-[#e6ba55] px-3 py-1 bg-[#e6ba55]/20 rounded-full border border-[#e6ba55]/40">
            TẦNG THƯỞNG ĐẶC BIỆT
          </span>
          <h2 className="text-xl sm:text-2xl font-serif font-black text-white mt-1.5 leading-tight">
            VÒNG QUAY MAY MẮN
          </h2>
          <p className="text-xs text-[#cdbdaf] mt-1 max-w-xs leading-relaxed">
            Bạn đã vượt qua {floor} tầng tháp! Hãy quay để nhận tim hồi phục hoặc tem bảo vật di sản.
          </p>

          {/* WHEEL STAGE */}
          <div className="relative w-full max-w-[340px] aspect-square mx-auto my-3 sm:my-4 flex items-center justify-center">
            {/* GILDED & RUBY NEEDLE POINTER */}
            <div className="absolute -top-3.5 z-30 flex flex-col items-center pointer-events-none drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
              <div className="w-5 h-5 rounded-full bg-gradient-to-b from-[#e6ba55] via-[#a50021] to-[#590011] border-2 border-[#fff4dd] shadow flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              </div>
              <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[20px] border-t-[#a50021] -mt-1" />
            </div>

            {/* ROTATING LACQUER WHEEL DISC */}
            <div
              className="w-full h-full rounded-full border-4 sm:border-[5px] border-[#e6ba55] shadow-[0_12px_36px_rgba(0,0,0,0.7)] relative overflow-hidden flex items-center justify-center bg-[#1b1116]"
              style={{
                transform: `rotate(${wheelRotation}deg)`,
                transition: isSpinning ? 'transform 4s cubic-bezier(0.14, 0.65, 0.12, 1)' : 'none',
                backgroundImage: 'url(/images/generated/lucky-wheel-lacquer-v1.webp)',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              {LUCKY_WHEEL_PRIZES.map((p, idx) => {
                const positions = [
                  { left: '67%', top: '33%' },
                  { left: '67%', top: '67%' },
                  { left: '33%', top: '67%' },
                  { left: '33%', top: '33%' },
                ];
                const pos = positions[idx];

                return (
                  <div
                    key={p.id}
                    style={{
                      left: pos.left,
                      top: pos.top,
                      transform: 'translate(-50%, -50%)',
                    }}
                    className="absolute flex flex-col items-center justify-center w-[25%] h-[27%] text-center pointer-events-none select-none"
                  >
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-8 h-8 sm:w-10 sm:h-10 object-contain filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                    />
                    <span className="mt-1 max-w-[92%] px-1.5 py-0.5 rounded-full bg-[#fff9e6]/95 border border-[#9a7229]/40 text-[9px] sm:text-[10px] font-extrabold text-[#3b2416] leading-tight shadow-sm truncate">
                      {p.name}
                    </span>
                  </div>
                );
              })}

              {/* CENTER HUB MEDALLION */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[18%] aspect-square rounded-full bg-gradient-to-tr from-[#790019] to-[#b80026] border-2 sm:border-3 border-[#ffefc7] shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex items-center justify-center z-10">
                <span className="text-[10px] sm:text-xs font-serif font-black text-[#ffefc7] tracking-wider drop-shadow">
                  VNFAM
                </span>
              </div>
            </div>
          </div>

          {/* RESULT CARD HOẶC NÚT QUAY */}
          {prizeWon ? (
            <div className="space-y-3 w-full animate-scale-up max-w-sm mx-auto">
              <div className="p-4 bg-gradient-to-b from-[#2a1721] to-[#1c1016] rounded-2xl border-2 border-[#e6ba55] shadow-[0_0_24px_rgba(230,186,85,0.4)] space-y-2 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-radial from-[#e6ba55]/15 to-transparent pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center">
                  <img
                    src={prizeWon.image}
                    alt={prizeWon.name}
                    className="w-14 h-14 sm:w-16 sm:h-16 object-contain filter drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)] animate-bounce"
                  />
                  <span className="text-[11px] font-bold text-[#e6ba55] uppercase tracking-wider mt-1 block">
                    🎉 PHẦN THƯỞNG CỦA BẠN
                  </span>
                  <strong className="text-xl sm:text-2xl font-serif font-black text-[#ffe4a0] block mt-0.5">
                    {prizeWon.name}
                  </strong>
                  <p className="text-xs text-[#cdbdaf] mt-1 max-w-xs leading-relaxed">
                    {prizeWon.desc}
                  </p>

                  <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center justify-center gap-2 text-xs text-[#fff4dd]">
                    <span>Sinh lực hiện tại:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3].map((h) => (
                        <Heart
                          key={h}
                          className={`w-4 h-4 ${
                            h <= hearts
                              ? 'text-[#e6ba55] fill-[#e6ba55] drop-shadow'
                              : 'text-[#5a4249] fill-[#231419]'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={advanceToNextFloor}
                className="w-full gf-button gf-button-gold py-3.5 text-sm sm:text-base flex items-center justify-center gap-2 font-bold shadow-2xl active:scale-98"
              >
                <span>TIẾP TỤC LEO THÁP (TẦNG {floor + 1})</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="w-full max-w-sm mx-auto space-y-2">
              <button
                onClick={handleSpinWheel}
                disabled={isSpinning}
                className={`w-full gf-button gf-button-gold py-3.5 sm:py-4 text-base sm:text-lg flex items-center justify-center gap-2 font-black shadow-2xl transition-all duration-200 ${
                  isSpinning
                    ? 'opacity-80 cursor-not-allowed'
                    : 'hover:scale-102 active:scale-98 shadow-[0_0_20px_rgba(230,186,85,0.4)]'
                }`}
              >
                <span>{isSpinning ? 'ĐANG QUAY THƯỞNG… 🎡' : 'QUAY THƯỞNG NGAY 🎡'}</span>
              </button>
              <p className="text-[11px] text-[#cdbdaf]">
                Mỗi mốc 7 tầng bạn sẽ nhận được 1 lượt quay may mắn.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ============================================================
          GAME OVER: HẾT TIM
          ============================================================ */}
      {gameStatus === 'lost' && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-sm mx-auto w-full text-center space-y-4 animate-scale-up pb-[max(1rem,env(safe-area-inset-bottom,16px))] bg-[#1c1217]">
          <div className="w-16 h-16 rounded-full bg-[#4a1c24] text-[#ef5350] border border-[#ef5350] flex items-center justify-center shadow-lg mx-auto">
            <Heart className="w-8 h-8 fill-[#ef5350]" />
          </div>
          <div>
            <h2 className="text-2xl font-serif font-bold text-white">Bạn đã hết tim!</h2>
            <p className="text-xs text-[#cdbdaf] mt-1">
              Bạn dừng chân tại <strong>Tầng {floor}</strong> với {correctAnswers} câu trả lời đúng.
            </p>
          </div>
          <button
            onClick={handleRestart}
            className="w-full gf-button gf-button-gold py-3 text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Thử lại từ tầng 1</span>
          </button>
          <button
            onClick={onClose}
            className="w-full gf-button gf-button-secondary text-xs py-2.5"
          >
            Quay lại trang chính
          </button>
        </div>
      )}

      {/* ============================================================
          VICTORY: CHINH PHỤC ĐỈNH THÁP
          ============================================================ */}
      {gameStatus === 'victory' && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-sm mx-auto w-full text-center space-y-4 animate-scale-up pb-[max(1rem,env(safe-area-inset-bottom,16px))] bg-[#1c1217]">
          <div className="w-20 h-20 rounded-full bg-[#e6ba55]/20 text-[#e6ba55] border-2 border-[#e6ba55] flex items-center justify-center shadow-2xl mx-auto animate-bounce">
            <Trophy className="w-10 h-10" />
          </div>
          <div>
            <span className="text-xs font-bold text-[#e6ba55] uppercase tracking-wider block">
              CHÚC MỪNG CHIẾN THẮNG
            </span>
            <h2 className="text-3xl font-serif font-black text-white mt-1">
              ĐỈNH THÁP MỸ THUẬT!
            </h2>
            <p className="text-xs text-[#cdbdaf] mt-1.5">
              Bạn đã hoàn thành xuất sắc 10 tầng thử thách nghệ thuật và ghi danh trên bảng vàng.
            </p>
          </div>
          <button
            onClick={onOpenLeaderboard}
            className="w-full gf-button gf-button-gold py-3 text-sm flex items-center justify-center gap-2 shadow-xl"
          >
            <Trophy className="w-4 h-4" />
            <span>Xem Bảng xếp hạng Top 100</span>
          </button>
          <button
            onClick={handleRestart}
            className="w-full gf-button gf-button-secondary text-xs py-2.5"
          >
            Chơi lại lượt mới
          </button>
        </div>
      )}

      {/* ============================================================
          MODAL TÚI VẬT PHẨM (INVENTORY)
          ============================================================ */}
      {showInventoryModal && (
        <div className="modal-overlay show">
          <div className="modal-card">
            <div className="flex items-center justify-center gap-2.5 mb-1">
              <img
                src="/assets/item-bag.webp"
                alt="Túi vật phẩm"
                className="w-8 h-8 object-contain drop-shadow"
                draggable={false}
              />
              <h2 className="text-xl font-serif font-black text-[#ffe4a0]">
                TÚI VẬT PHẨM
              </h2>
            </div>
            <p className="text-xs font-semibold mb-3 text-[#cdbdaf]">
              Sưu tầm tem và bảo vật từ vòng quay may mắn
            </p>
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {inventory.length === 0 ? (
                <div className="col-span-2 py-6 text-center text-xs text-[#cdbdaf]">
                  <img
                    src="/assets/item-bag.webp"
                    alt="Túi trống"
                    className="w-16 h-16 object-contain mx-auto mb-2 opacity-70 drop-shadow"
                    draggable={false}
                  />
                  Túi đồ còn trống. Hãy vượt 7 tầng tháp để quay thưởng tem di sản!
                </div>
              ) : (
                inventory.map((item) => (
                  <div key={item.id} className="inventory-card !p-2.5 flex flex-col items-center">
                    <img src={item.image} alt={item.name} className="w-12 h-12 object-contain mb-1" />
                    <span className="text-xs font-bold text-center text-[#ffe4a0]">{item.name}</span>
                  </div>
                ))
              )}
            </div>
            <button
              className="btn-primary w-full"
              onClick={() => {
                sfx.tap();
                setShowInventoryModal(false);
              }}
            >
              Đóng
            </button>
          </div>
        </div>
      )}

      {/* ============================================================
          LIGHTBOX ZOOM MODAL (PHÓNG TO TÁC PHẨM)
          ============================================================ */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-3 sm:p-6 animate-fade-in select-none"
          onClick={() => setZoomImage(null)}
        >
          <button
            onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all z-10"
            aria-label="Đóng xem chi tiết"
          >
            <XIcon className="w-6 h-6" />
          </button>

          <div
            className="max-w-4xl max-h-[80vh] w-full flex items-center justify-center p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomImage}
              alt="Chi tiết kiệt tác"
              className="max-w-full max-h-[75vh] object-contain rounded-lg border border-[#8e7148]/60 shadow-2xl"
            />
          </div>

          {currentQ.imageCaption && (
            <p className="mt-3 text-xs sm:text-sm text-[#e6ba55] text-center font-serif font-medium px-4 max-w-xl">
              {currentQ.imageCaption}
            </p>
          )}

          <span className="text-[10px] text-white/50 mt-1">
            Chạm vào nền hoặc nút đóng để quay lại
          </span>
        </div>
      )}
    </div>
  );
};
